import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import AppConfig from '../../appConfig';
import { setAuthTokenGetter } from '../../actions/authToken';
import InitDataFetcher from './InitDataFetcher';

/**
 * Resolves Auth0 user to Portal userId (EmployeeId) and stores in localStorage.
 * Only rendered when Auth0 is enabled (inside Auth0Provider).
 */
/** Extract Portal userId from roles array (look for "id:GUID", "employeeId:GUID", or "userid:GUID"). */
function getUserIdFromRoles(roles) {
    if (!Array.isArray(roles)) return null;
    const idRole = roles.find(r => typeof r === 'string' && /^(id|employeeId):\s*[\w-]+$/i.test(r));
    if (idRole) return idRole.replace(/^(id|employeeId):\s*/i, '').trim();
    const userIdRole = roles.find(r => typeof r === 'string' && /^userid:\s*[\w-]+$/i.test(r));
    if (userIdRole) return userIdRole.replace(/^userid:\s*/i, '').trim();
    return null;
}

const UserIdResolver = ({ children }) => {
    const { isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        const audience = AppConfig.auth0.audience;
        setAuthTokenGetter(() =>
            audience
                ? getAccessTokenSilently({ authorizationParams: { audience } })
                : getAccessTokenSilently()
        );
        if (!isAuthenticated) {
            localStorage.removeItem('userId');
            setResolved(true);
            return;
        }

        const cached = localStorage.getItem('userId');
        if (cached) {
            setResolved(true);
            return;
        }

        let cancelled = false;

        const resolveUser = async () => {
            const setUserId = (userId) => {
                if (userId && !cancelled) localStorage.setItem('userId', userId);
            };

            // 1) Try ID token first (no API call, no 404 if API not deployed)
            try {
                const claims = await getIdTokenClaims();
                if (claims && typeof claims === 'object') {
                    let userId = null;
                    const roles = claims['https://irspl.auth0.com/roles'] ?? claims.roles ?? claims.app_metadata?.roles;
                    if (roles !== undefined) userId = getUserIdFromRoles(Array.isArray(roles) ? roles : [roles]);
                    if (!userId) {
                        for (const key of Object.keys(claims)) {
                            const val = claims[key];
                            if (Array.isArray(val)) userId = getUserIdFromRoles(val);
                            if (userId) break;
                        }
                    }
                    if (userId) {
                        setUserId(userId);
                        if (!cancelled) setResolved(true);
                        return;
                    }
                }
            } catch (_) { /* ignore */ }

            // 2) Fallback: call API (only if ID token didn't have roles)
            try {
                const audience = AppConfig.auth0.audience;
                const token = audience
                    ? await getAccessTokenSilently({ authorizationParams: { audience } })
                    : await getAccessTokenSilently();
                const baseUrl = AppConfig.env('url');
                const response = await axios.get(`${baseUrl}Auth/ResolveUserId`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled && response?.data?.userId) {
                    setUserId(response.data.userId);
                }
            } catch (err) {
                if (!cancelled) {
                    console.warn('Could not resolve userId from Auth0:', err?.response?.data || err.message);
                }
            } finally {
                if (!cancelled) setResolved(true);
            }
        };

        resolveUser();
        return () => { cancelled = true; };
    }, [isAuthenticated, getAccessTokenSilently]);

    // Show loading until we've attempted resolution (or found cache)
    if (!resolved) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                <p>Loading your profile...</p>
            </div>
        );
    }

    return (
        <>
            <InitDataFetcher />
            {children}
        </>
    );
};

export default UserIdResolver;
